import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { initNative } from '@/lib/native';
import { armTamperShield } from '@/lib/shield';
import { initTheme } from '@/lib/theme';
import { pullPrefs } from '@/lib/prefs';
import { Toaster } from 'sonner';
import Layout from '@/components/Layout';
import Dashboard from '@/pages/Dashboard';
import WaiverGate from '@/components/WaiverGate';
import RoleGate, { getRole } from '@/components/RoleGate';
import { InstallProvider } from '@/lib/install';
import { initModelHeaders } from '@/lib/model';

initModelHeaders();
import '@/App.css';

const LiveMode = lazy(() => import('@/pages/LiveMode'));
const ProfileAnalyzer = lazy(() => import('@/pages/ProfileAnalyzer'));
const BodyLanguage = lazy(() => import('@/pages/BodyLanguage'));
const History = lazy(() => import('@/pages/History'));
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));
const GatePage = lazy(() => import('@/pages/GatePage'));
const InstallGuide = lazy(() => import('@/pages/InstallGuide'));
const Share = lazy(() => import('@/pages/Share'));
const GuardianCircle = lazy(() => import('@/pages/GuardianCircle'));
const FacebookKit = lazy(() => import('@/pages/FacebookKit'));
const Credits = lazy(() => import('@/pages/Credits'));
const ShoppersBuddy = lazy(() => import('@/pages/ShoppersBuddy'));
const Couples = lazy(() => import('@/pages/Couples'));
const Reel = lazy(() => import('@/pages/Reel'));
const Terms = lazy(() => import('@/pages/Terms'));
const Family = lazy(() => import('@/pages/Family'));
const Growth = lazy(() => import('@/pages/Growth'));
const Creator = lazy(() => import('@/pages/Creator'));
const GiftRedeem = lazy(() => import('@/pages/GiftRedeem'));
const Account = lazy(() => import('@/pages/Account'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const AutoGuard = lazy(() => import('@/pages/AutoGuard'));
const VideoAnalyzer = lazy(() => import('@/pages/VideoAnalyzer'));
const Tactics = lazy(() => import('@/pages/Tactics'));
const Commercial = lazy(() => import('@/pages/Commercial'));
const TruthFinder = lazy(() => import('@/pages/TruthFinder'));
const Settings = lazy(() => import('@/pages/Settings'));
const Enlightenment = lazy(() => import('@/pages/Enlightenment'));
const FamilyLink = lazy(() => import('@/pages/FamilyLink'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('@/pages/PaymentCancel'));

const Fallback = () => <div className="min-h-[40vh] flex items-center justify-center"><div className="h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

function NativeBridge() {
  const navigate = useNavigate();
  useEffect(() => initNative(navigate), [navigate]);
  useEffect(() => { armTamperShield(); pullPrefs(); initTheme(); }, []);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/install" element={<InstallGuide />} />
            <Route path="/share" element={<Share />} />
            <Route path="/gate" element={<GatePage />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/commercial" element={<Commercial />} />
            <Route path="/account" element={<Shell><Account /></Shell>} />
            <Route path="/legal" element={<Shell><Terms /></Shell>} />
            <Route path="/gift" element={<GiftRedeem />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route element={<GateGuard><WaiverGate><RoleGate><Layout /></RoleGate></WaiverGate></GateGuard>}>
              <Route index element={<Dashboard />} />
              <Route path="/live" element={<LiveMode />} />
              <Route path="/autoguard" element={<AutoGuard />} />
              <Route path="/profile" element={<ProfileAnalyzer />} />
              <Route path="/body" element={<BodyLanguage />} />
              <Route path="/video" element={<VideoAnalyzer />} />
              <Route path="/tactics" element={getRole() === 'child' ? <Navigate to="/" replace /> : <Tactics />} />
              <Route path="/truth" element={<TruthFinder />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/enlightenment" element={<Enlightenment />} />
              <Route path="/link" element={<FamilyLink />} />
              <Route path="/guardian" element={<GuardianCircle />} />
              <Route path="/facebook" element={<FacebookKit />} />
              <Route path="/reel" element={<Reel />} />
              <Route path="/family" element={<Family />} />
              <Route path="/growth" element={<Growth />} />
              <Route path="/creator" element={<Creator />} />
              <Route path="/credits" element={<Credits />} />
              <Route path="/shop" element={<ShoppersBuddy />} />
              <Route path="/couples" element={<Couples />} />
              <Route path="/history" element={<History />} />
              <Route path="/docs" element={<ApiDocs />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 font-mono px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <Link to="/" className="mb-6 inline-block text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-zinc-300">← back to app</Link>
        {children}
      </div>
    </div>
  );
}

function GateGuard({ children }) {
  // No lock screen. 3 free tries, then a regular-price 30-day pass. (/gate stays reachable by link only.)
  return children;
}

function App() {
  return (
    <div className="dark">
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: '#18181b', border: '1px solid #27272a', color: '#fafafa', borderRadius: 2, fontFamily: 'IBM Plex Mono' },
      }} />
      <InstallProvider>
        <BrowserRouter>
          <NativeBridge />
          <Suspense fallback={<Fallback />}><AppRoutes /></Suspense>
        </BrowserRouter>
      </InstallProvider>
    </div>
  );
}

export default App;
